import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * CustomerCombobox — drop-in replacement for the old free-text "Customer Name"
 * Input. Renders a Select of existing customers + an inline "+ Add new" option
 * that opens a small dialog to create a customer master record.
 *
 * Props:
 *   value:    current customer name (string)
 *   onChange: (newName) => void
 *   testid:   data-testid prefix (optional)
 */
export default function CustomerCombobox({ value, onChange, testid = "customer-combobox" }) {
    const [customers, setCustomers] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState({ name: "", contact_person: "", email: "", phone: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const r = await api.get(`/customers`);
            setCustomers(r.data || []);
        } catch { /* silent */ }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const name = (form.name || "").trim();
        if (!name) { toast.error("Customer name required"); return; }
        setSaving(true);
        try {
            const r = await api.post(`/customers`, { ...form, name });
            toast.success(`Customer "${r.data.name}" saved`);
            await load();
            onChange?.(r.data.name);
            setAddOpen(false);
            setForm({ name: "", contact_person: "", email: "", phone: "" });
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    // Allow free typing if value isn't a known customer name (backward compatibility
    // with parts that had a customer_name typed in earlier).
    const isKnown = customers.some((c) => c.name === value);

    return (
        <>
            <div className="flex gap-2">
                <Select value={isKnown ? value : "__none__"} onValueChange={(v) => { if (v === "__add__") { setAddOpen(true); setForm((f) => ({ ...f, name: "" })); return; } if (v === "__none__") { onChange?.(""); return; } onChange?.(v); }}>
                    <SelectTrigger className="rounded-none flex-1" data-testid={`${testid}-select`}>
                        <SelectValue placeholder="Pick a customer">
                            {value || <span className="text-zinc-400">Pick a customer</span>}
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__" data-testid={`${testid}-none`}>— None —</SelectItem>
                        {customers.map((c) => (
                            <SelectItem key={c.customer_id} value={c.name} data-testid={`${testid}-opt-${c.customer_id}`}>
                                {c.name}
                            </SelectItem>
                        ))}
                        <SelectItem value="__add__" data-testid={`${testid}-add-row`}>
                            <span className="text-[#002FA7] font-medium">+ Add new customer…</span>
                        </SelectItem>
                    </SelectContent>
                </Select>
                {value && !isKnown && (
                    <button type="button" onClick={() => { setForm({ name: value, contact_person: "", email: "", phone: "" }); setAddOpen(true); }} className="text-[10px] uppercase tracking-wider text-[#FFB703] hover:underline px-2 whitespace-nowrap" title="This customer isn't in the master list — click to save it" data-testid={`${testid}-promote`}>
                        save&nbsp;&ldquo;{value}&rdquo;
                    </button>
                )}
            </div>

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="rounded-none max-w-md">
                    <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
                    <div className="space-y-3" data-testid={`${testid}-add-dialog`}>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Name *</label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid={`${testid}-add-name`} autoFocus />
                            <p className="text-[10px] text-zinc-400 mt-1">Customer master prevents typo-duplicates. Segment / status stay on each Part.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-zinc-500">Contact person</label>
                                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-none" />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-zinc-500">Phone</label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-[11px] uppercase tracking-wider text-zinc-500">Email</label>
                                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setAddOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" disabled={saving} onClick={save} data-testid={`${testid}-save`}>
                            <Plus size={14} className="mr-2" /> {saving ? "Saving…" : "Save customer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
